import React from 'react';
import { Button, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { PlanningSelectionInfo, PlanningActionType } from '../../../../types/planManagement';
import {
  isShadeStructureAction,
  SHADE_STRUCTURE_ACTIONS,
  SHADE_STRUCTURE_PRESETS,
} from '../planningStructuralObjects';

const { Text } = Typography;
type PlanningPlacementTool = Exclude<PlanningActionType, 'delete'>;

interface SelectionRect {
  rows: number;
  cols: number;
  exceedsBoundary?: boolean;
}

interface PlanningModeContentProps {
  allowBedSelection: boolean;
  isBusy: boolean;
  planningSelection: PlanningSelectionInfo | null;
  selectionRect: SelectionRect | null;
  onPlanningAction: (action: PlanningActionType) => void;
  onCancelPlanningSelection: () => void;
}

const PlanningModeContent: React.FC<PlanningModeContentProps> = ({
  allowBedSelection,
  isBusy,
  planningSelection,
  selectionRect,
  onPlanningAction,
  onCancelPlanningSelection,
}) => {
  const [selectedTool, setSelectedTool] = React.useState<PlanningPlacementTool>('create-bed');

  if (!allowBedSelection) {
    return <Text type="secondary">Нажмите на участок для перехода</Text>;
  }

  if (!planningSelection && !selectionRect) {
    return (
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Text type="secondary">1) Выберите, что ставим. 2) Выделите клетки. 3) Подтвердите справа.</Text>
        <Space wrap>
          <Button type={selectedTool === 'create-bed' ? 'primary' : 'default'} onClick={() => setSelectedTool('create-bed')} disabled={isBusy}>Грядка</Button>
          <Button type={selectedTool === 'fill-path' ? 'primary' : 'default'} onClick={() => setSelectedTool('fill-path')} disabled={isBusy}>Дорожка</Button>
          <Button type={selectedTool === 'fill-obstacle' ? 'primary' : 'default'} onClick={() => setSelectedTool('fill-obstacle')} disabled={isBusy}>Изгородь</Button>
          <Button type={selectedTool === 'greenhouse' ? 'primary' : 'default'} onClick={() => setSelectedTool('greenhouse')} disabled={isBusy}>Теплица</Button>
          {SHADE_STRUCTURE_ACTIONS.map((action) => {
            const preset = SHADE_STRUCTURE_PRESETS[action];
            return (
              <Button
                key={action}
                type={selectedTool === action ? 'primary' : 'default'}
                onClick={() => setSelectedTool(action)}
                disabled={isBusy}
                title={preset.hint}
              >
                {preset.label}
              </Button>
            );
          })}
        </Space>
      </Space>
    );
  }

  if (selectionRect && !planningSelection) {
    return <Text type="secondary">Выделение: {selectionRect.rows} × {selectionRect.cols} ({selectionRect.rows * selectionRect.cols} клеток)</Text>;
  }

  if (!planningSelection) return null;

  const parts: string[] = [];
  if (planningSelection.emptyCellCount > 0) parts.push(`${planningSelection.emptyCellCount} пустых`);
  if (planningSelection.beds.length > 0) parts.push(`${planningSelection.beds.length} грядок`);
  if (planningSelection.pathCellCount > 0) parts.push(`${planningSelection.pathCellCount} дорожек`);
  if (planningSelection.obstacleCellCount > 0) parts.push(`${planningSelection.obstacleCellCount} изгородей`);
  if (planningSelection.structuralObjectIds.length > 0) parts.push(`${planningSelection.structuralObjectIds.length} объектов`);
  const summary = `${planningSelection.totalCells} клеток (${parts.join(', ')})`;

  const hasBeds = planningSelection.beds.length > 0;
  const hasPaths = planningSelection.pathCellCount > 0;
  const hasObstacles = planningSelection.obstacleCellCount > 0;
  const hasStructuralObjects = planningSelection.structuralObjectIds.length > 0;
  const hasStructuralObjectBoundaryCrossing = planningSelection.structuralObjectBoundaryCrossingCellCount > 0;

  const toolValidation = (() => {
    if (selectionRect?.exceedsBoundary) {
      return { valid: false, message: 'Выделение выходит за границы участка' };
    }

    if (selectedTool === 'create-bed') {
      if (hasBeds) return { valid: false, message: 'Поверх грядок можно ставить только теплицу' };
      if (hasObstacles) return { valid: false, message: 'Поверх изгороди ставить нельзя' };
      if (hasStructuralObjectBoundaryCrossing) return { valid: false, message: 'Грядка не должна пересекать границу теплицы или другого объекта' };
      return { valid: true, message: hasPaths ? 'Дорожки в выделении будут заменены грядкой' : null };
    }

    if (selectedTool === 'fill-path') {
      if (hasBeds) return { valid: false, message: 'Поверх грядок можно ставить только теплицу' };
      if (hasObstacles) return { valid: false, message: 'Поверх изгороди ставить нельзя' };
      if (hasStructuralObjectBoundaryCrossing) return { valid: false, message: 'Дорожка не должна пересекать границу теплицы или другого объекта' };
      return { valid: true, message: null };
    }

    if (selectedTool === 'fill-obstacle') {
      if (hasBeds) return { valid: false, message: 'Поверх грядок можно ставить только теплицу' };
      if (hasObstacles) return { valid: false, message: 'Поверх изгороди ставить нельзя' };
      if (hasStructuralObjects) return { valid: false, message: 'Выделение уже содержит объект' };
      return { valid: true, message: hasPaths ? 'Дорожки в выделении будут заменены изгородью' : null };
    }

    if (selectedTool === 'greenhouse') {
      if (hasObstacles) return { valid: false, message: 'Нельзя ставить теплицу поверх изгороди' };
      if (hasStructuralObjects) return { valid: false, message: 'Нельзя ставить теплицу поверх другого объекта' };
      const coveredBeds = planningSelection.beds.length;
      const coveredPaths = planningSelection.pathCellCount;
      const desc = [coveredBeds > 0 && `${coveredBeds} грядок`, coveredPaths > 0 && `${coveredPaths} дорожек`].filter(Boolean).join(' и ');
      return { valid: true, message: desc ? `Теплица накроет: ${desc}` : 'Теплица накроет выделение' };
    }

    if (isShadeStructureAction(selectedTool)) {
      const preset = SHADE_STRUCTURE_PRESETS[selectedTool];
      if (hasBeds) return { valid: false, message: 'Нельзя ставить объект поверх грядки' };
      if (hasObstacles) return { valid: false, message: 'Нельзя ставить объект поверх изгороди' };
      if (hasStructuralObjects) return { valid: false, message: 'Выделение уже содержит объект' };
      if (preset.singleCell && planningSelection.totalCells !== 1) {
        return { valid: false, message: `${preset.label} устанавливается на одну клетку` };
      }
      return {
        valid: true,
        message: hasPaths
          ? `${preset.label} будет установлен поверх выбранной дорожки`
          : preset.hint,
      };
    }

    return { valid: false, message: 'Инструмент недоступен' };
  })();

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Text>{summary}</Text>

      <Space wrap>
        <Button type={selectedTool === 'create-bed' ? 'primary' : 'default'} onClick={() => setSelectedTool('create-bed')} disabled={isBusy}>Грядка</Button>
        <Button type={selectedTool === 'fill-path' ? 'primary' : 'default'} onClick={() => setSelectedTool('fill-path')} disabled={isBusy}>Дорожка</Button>
        <Button type={selectedTool === 'fill-obstacle' ? 'primary' : 'default'} onClick={() => setSelectedTool('fill-obstacle')} disabled={isBusy}>Изгородь</Button>
        <Button type={selectedTool === 'greenhouse' ? 'primary' : 'default'} onClick={() => setSelectedTool('greenhouse')} disabled={isBusy}>Теплица</Button>
        {SHADE_STRUCTURE_ACTIONS.map((action) => {
          const preset = SHADE_STRUCTURE_PRESETS[action];
          return (
            <Button
              key={action}
              type={selectedTool === action ? 'primary' : 'default'}
              onClick={() => setSelectedTool(action)}
              disabled={isBusy}
              title={preset.hint}
            >
              {preset.label}
            </Button>
          );
        })}
      </Space>

      {toolValidation.message && (
        <Text type={toolValidation.valid ? 'secondary' : 'danger'}>{toolValidation.message}</Text>
      )}

      <Space wrap>
        <Button
          type="primary"
          disabled={isBusy || !toolValidation.valid}
          onClick={() => onPlanningAction(selectedTool)}
        >
          Подтвердить установку
        </Button>
        <Button danger icon={<DeleteOutlined />} onClick={() => onPlanningAction('delete')} disabled={isBusy}>
          Удалить выделение
        </Button>
        <Button onClick={onCancelPlanningSelection} disabled={isBusy}>Сбросить выделение</Button>
      </Space>
    </Space>
  );
};

export default PlanningModeContent;
